/* eslint-disable @typescript-eslint/no-namespace */
import './PricingScreen.less';
import React, { useEffect, useState, useRef, useMemo } from 'react';
import Meta from '../../components/Meta';
import {
  getCustomerSession,
  CustomerSessionResponse,
  CustomerSessionNotFoundError,
} from '../../services/customerService';
import {
  getUserId,
  getOrgId,
  convertSubscriptionDataToStripeSubscription,
} from '../../services/subscription';
import { useAbility } from '../../hooks/useAbility';
import { Resource } from '../../enums/common';
import { useSelector } from 'react-redux';
import { RootState } from '../../app/store';
import { useUserAccess } from '../../hooks/useUserAccess';
import { Link } from 'react-router-dom';
import { getTeamName } from '../../services/account.service';

// If using TypeScript, add the following snippet to your file as well.
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'stripe-pricing-table': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      >;
    }
  }
}

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <>
      <Meta name="Cirra AI - Pricing" />
      <div className="main-screen-element" style={{ padding: 0 }}>
        <div id="pricing-container">{children}</div>
      </div>
    </>
  );
};

const PricingScreen: React.FC = () => {
  const [customerSession, setCustomerSession] =
    useState<CustomerSessionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasFetchedSession = useRef(false);
  const { currentTeamAccount } = useUserAccess();
  const { currentAbility } = useAbility();
  const { subscriptionInfo, loading: isLoadingSubscription } = useSelector(
    (state: RootState) => state.subscription
  );

  const canManageSubscription = useMemo(() => {
    return (
      !isLoadingSubscription &&
      currentAbility &&
      subscriptionInfo &&
      currentAbility.can(
        'manage',
        Resource.Subscription,
        convertSubscriptionDataToStripeSubscription(
          subscriptionInfo.subscription
        )
      )
    );
  }, [currentAbility, isLoadingSubscription, subscriptionInfo]);

  // Load Stripe configuration from environment variables
  const personalPricingTableId =
    process.env.REACT_APP_STRIPE_PERSONAL_PRICING_TABLE_ID;
  const teamPricingTableId = process.env.REACT_APP_STRIPE_TEAM_PRICING_TABLE_ID;
  const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

  // Derive team context from Redux — getOrgId() reads from subscriptionInfo
  // which is null for users without a subscription (the users viewing this page).
  const currentTeam = useSelector(
    (state: RootState) => state.team.currentTeam.record
  );
  const isTeamContext = !!currentTeam;

  // Get current Cirra user and organization IDs for client reference ID
  // `client-reference-id` must be a string and can only contain alphanumeric characters, spaces, dashes and underscores.
  // So we cannot use userId which contains '@' and possibly other special characters
  const userId = getUserId() || 'unknown_user'; // ID or UserInfo record in database
  const orgId = getOrgId() || currentTeam?.id || null; // Internal (Mongo) ID for the organization
  const clientReferenceId = userId
    ? orgId
      ? `${userId}_${orgId}`
      : userId
    : null;

  useEffect(() => {
    const fetchCustomerSession = async () => {
      setLoading(true);
      // Prevent duplicate calls
      if (hasFetchedSession.current) {
        return;
      }

      try {
        hasFetchedSession.current = true;
        const session = await getCustomerSession();
        setCustomerSession(session);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch customer session:', err);
        // If customer session is not found, continue without it
        // Only set error for other types of failures
        if (err instanceof CustomerSessionNotFoundError) {
          console.log('Customer session not found, proceeding without it');
          setCustomerSession(null);
          setError(null);
        } else {
          setError(
            err instanceof Error
              ? err.message
              : 'Failed to fetch customer session'
          );
        }
      } finally {
        setLoading(false);
      }
    };
    if (!isLoadingSubscription) {
      if (canManageSubscription) {
        fetchCustomerSession();
      } else {
        setLoading(false);
      }
    }
  }, [isLoadingSubscription, canManageSubscription]);

  // Check if required environment variables are available
  if (!personalPricingTableId || !teamPricingTableId || !publishableKey) {
    console.error('Missing required Stripe environment variables:', {
      personalPricingTableId: !!personalPricingTableId,
      teamPricingTableId: !!teamPricingTableId,
      publishableKey: !!publishableKey,
    });

    // In production, show a more user-friendly message
    if (process.env.NODE_ENV === 'production') {
      return (
        <Layout>
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <h2>Pricing Information</h2>
            <p>Please contact our sales team for pricing information.</p>
            <p>Email: sales@cirra.ai</p>
          </div>
        </Layout>
      );
    }

    return (
      <Layout>
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h2>Pricing Configuration Error</h2>
          <p>Unable to load pricing information. Please contact support.</p>
          <div
            style={{
              marginTop: '1rem',
              textAlign: 'left',
              fontSize: '12px',
            }}
          >
            <p>
              <strong>Debug Info:</strong>
            </p>
            <p>
              REACT_APP_STRIPE_PERSONAL_PRICING_TABLE_ID:{' '}
              {personalPricingTableId ? 'SET' : 'MISSING'}
            </p>
            <p>
              REACT_APP_STRIPE_TEAM_PRICING_TABLE_ID:{' '}
              {teamPricingTableId ? 'SET' : 'MISSING'}
            </p>
            <p>
              REACT_APP_STRIPE_PUBLISHABLE_KEY:{' '}
              {publishableKey ? 'SET' : 'MISSING'}
            </p>
            <p>NODE_ENV: {process.env.NODE_ENV}</p>
            <p>
              API_BASE_URL: {process.env.REACT_APP_API_BASE_URL || 'NOT SET'}
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h2>Loading Pricing Information...</h2>
          <p>Please wait while we load your personalized pricing options.</p>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h2>Unable to Load Pricing</h2>
          <p>{error}</p>
          <p>
            Please try refreshing the page or contact support if the issue
            persists.
          </p>
        </div>
      </Layout>
    );
  }

  if (!canManageSubscription) {
    return (
      <Layout>
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h2>Unsufficient permissions</h2>
          {currentTeamAccount ? (
            <p>
              You do not have permission to manage the subscription of team{' '}
              {getTeamName(currentTeamAccount.team)}.<br />
              Please contact your team onwer.
            </p>
          ) : (
            <p>
              Something went wrong. Please contact{' '}
              <Link to="mailto:support@cirra.ai">support@cirra.ai</Link>
            </p>
          )}
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <stripe-pricing-table
        pricing-table-id={
          isTeamContext ? teamPricingTableId : personalPricingTableId
        }
        publishable-key={publishableKey}
        customer-session-client-secret={customerSession?.client_secret}
        client-reference-id={clientReferenceId}
      />
    </Layout>
  );
};

export default PricingScreen;
